import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  addToCart as addToCartRequest,
  checkout as checkoutRequest,
  getCart,
  updateCartItem as updateCartItemRequest,
  removeItem as removeItemRequest,
} from "../services/cartService";
import { getGuestId } from "../utils/guest";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [guestId, setGuestIdState] = useState(() => getGuestId());

  const refreshGuestId = useCallback(() => {
    const nextGuestId = getGuestId();
    setGuestIdState(nextGuestId);
    return nextGuestId;
  }, []);

  const loadCart = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      refreshGuestId();
      const response = await getCart();
      setCartItems(response.items);
    } catch (loadError) {
      console.warn("Cart could not be loaded for saved GuestId. GuestId kept.", {
        status: loadError?.response?.status,
        message: loadError?.message,
      });
      setError("Cart could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [refreshGuestId]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setToast("");
    }, 2500);

    return () => window.clearTimeout(timer);
  }, [toast]);

  const addToCart = useCallback(
    async (productOrId, qty = 1) => {
      const response = await addToCartRequest(productOrId, qty);
      setCartItems(response.items);
      setToast("Added to cart");
      return response;
    },
    []
  );

  const removeItem = useCallback(async (item) => {
    const response = await removeItemRequest(item);
    setCartItems(response.items);
    return response;
  }, []);

  const updateQuantity = useCallback(async (item, quantity) => {
    if (quantity <= 0) {
      const response = await removeItemRequest(item);
      setCartItems(response.items);
      return response;
    }

    const response = await updateCartItemRequest(item, quantity);
    setCartItems(response.items);
    return response;
  }, []);

  const checkout = useCallback(async (orderData) => {
    const response = await checkoutRequest(orderData);
    setCartItems([]);
    return response;
  }, []);

  const resetCart = useCallback(() => {
    setCartItems([]);
    setError("");
    setToast("");
  }, []);

  const cartCount = useMemo(() => cartItems.length, [cartItems]);

  const cartSubtotal = useMemo(
    () => cartItems.reduce((total, item) => total + Number(item.total || 0), 0),
    [cartItems]
  );

  const cartGSTTotal = useMemo(
    () =>
      cartItems.reduce((total, item) => {
        const lineTotal = Number(item.total || Number(item.price || 0) * Number(item.qty || 0));
        return total + (item.gst ? lineTotal * 0.1 : 0);
      }, 0),
    [cartItems]
  );

  const cartGrandTotal = useMemo(
    () => cartSubtotal + cartGSTTotal,
    [cartGSTTotal, cartSubtotal]
  );

  const value = useMemo(
    () => ({
      cartItems,
      guestId,
      cartCount,
      loading,
      error,
      toast,
      cartSubtotal,
      cartGSTTotal,
      cartGrandTotal,
      cartTotal: cartGrandTotal,
      loadCart,
      addToCart,
      removeItem,
      updateQuantity,
      checkout,
      resetCart,
      refreshGuestId,
    }),
    [addToCart, cartCount, cartGSTTotal, cartGrandTotal, cartItems, cartSubtotal, checkout, error, guestId, loadCart, loading, refreshGuestId, removeItem, resetCart, toast, updateQuantity]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }

  return context;
}
